import React, { memo, useRef } from "react";
import { useImmer } from "use-immer";

import { newPostTestMetricsGraphConfig } from "@intelligems/ig-graphs/graph-configs";
import { StackedBarImpactColors } from "@intelligems/ig-graphs/colors";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { Checkbox, FormControlLabel, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { selectData } from "@src/api/analytics/dashboard-tinybird";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { BaseMetricSection } from "@src/features/analytics/baseDashboard/BaseMetricSection";
import { BaseGraphPaper } from "@src/features/analytics/baseDashboard/chartComponents/GraphPapers";
import { GraphsWrapper } from "@src/features/analytics/baseDashboard/chartComponents/GraphsWrapper";
import { useSetGridWidthAnalytics } from "@src/features/analytics/hooks/use-set-grid-width-analytics";
import { AnalyticsAudienceTableWrapper } from "@src/features/analytics/tables/analyticsAudienceTable/AnalyticsAudienceTableWrapper";
import { useDispatch, useSelector } from "@src/store";
import {
  getExperienceAnalyticsFilters,
  updateDashboardFilter,
} from "@src/store/slices/dashboardFilters";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";
import { UseQueryResult } from "@tanstack/react-query";

import { PostTestPageAlert } from "../PostTestPageAlert";

interface PostTestMetricsAnalyticsPageProps {
  data: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  totalExperienceVisitors: number;
  graphConfigs: GraphConfig[];
  testDuration: number;
  compareVariationId: string;
}

export const PostTestMetricsAnalyticsPage = memo(
  ({
    data,
    totalExperienceVisitors,
    graphConfigs,
    testDuration,
    compareVariationId,
  }: PostTestMetricsAnalyticsPageProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const experience = useSelector(selectedExperience);
    const variations = useSelector(selectedVariations);
    const { includeTestOrders } = useSelector(getExperienceAnalyticsFilters);
    const storeCurrencyConfig = useConfiguration<{
      code: string;
      locale: string;
    }>("shop_currency");

    const [, setHover] = useImmer(-1);
    const ref = useRef<HTMLDivElement | null>(null);
    const gridWidth = useSetGridWidthAnalytics(ref);

    return (
      <Stack ref={ref} direction="column" spacing={4}>
        <Grid>
          <BaseMetricSection
            groupTitle={"Post-Test Metrics"}
            showCompareGroups={true}
            rightSideButtons={[
              <FormControlLabel
                key="includeTestOrders"
                control={
                  <Checkbox
                    size="small"
                    checked={includeTestOrders ?? false}
                    onChange={(_, checked) =>
                      dispatch(
                        updateDashboardFilter({
                          key: "includeTestOrders",
                          value: checked,
                        })
                      )
                    }
                  />
                }
                label="Include In-Test Orders"
              />,
            ]}
          >
            <Grid size={12}>
              <PostTestPageAlert />
            </Grid>

            {graphConfigs.map((config, index) => (
              <Grid key={config.metrics[0]} size={gridWidth}>
                <BaseGraphPaper
                  onMouseEnter={() => setHover(index)}
                  onMouseLeave={() => setHover(-1)}
                  sx={{
                    height: "100%",
                  }}
                >
                  <GraphsWrapper
                    testDuration={testDuration}
                    totalTestVisitors={totalExperienceVisitors}
                    config={config}
                    renderLocation={"experiment"}
                    isDataLoading={data[0].isLoading || data[0].isPending}
                    data={selectData({
                      dashboardData: data,
                      config,
                      _resource: experience,
                      locale: storeCurrencyConfig?.value?.locale,
                      metricFilterIndex: 0,
                      variations,
                    })}
                    rawData={data}
                    compareVariationId={compareVariationId}
                    setBarCellColor={({ compareVariationId, data }) => {
                      if (data.title === "In-Test Customers") {
                        // uses lighter purple; all other charts here are post-test metrics
                        return compareVariationId === data.variation_id
                          ? theme.igPrimitives.blue[800]
                          : theme.igPrimitives.indigo[300];
                      }

                      return compareVariationId === data.variation_id
                        ? theme.igPrimitives.base[800]
                        : theme.igPrimitives.base[500];
                    }}
                    setStackedBarCellColor={({
                      compareVariationId,
                      data,
                      index,
                    }) => {
                      if (
                        data?.title?.includes?.("Post-Test") ||
                        data?.title?.includes?.("Total")
                      ) {
                        return compareVariationId === data.variation_id
                          ? StackedBarImpactColors[index === 0 ? 1 : 0][0]
                          : StackedBarImpactColors[index === 0 ? 1 : 0][1];
                      }

                      return data.variation_id === compareVariationId
                        ? StackedBarImpactColors[index][0]
                        : StackedBarImpactColors[index][1];
                    }}
                  />
                </BaseGraphPaper>
              </Grid>
            ))}
          </BaseMetricSection>
        </Grid>

        <Grid>
          <AnalyticsAudienceTableWrapper
            experience={experience}
            variations={variations}
            dashboardData={data}
            testDuration={testDuration}
            graphConfigs={newPostTestMetricsGraphConfig}
            excludeColumns={["n_orders", "net_revenue", "n_customers"]}
          />
        </Grid>
      </Stack>
    );
  }
);
